import axios from 'axios';
import toast from 'react-hot-toast';

// 1. Create Axios Instance
const api = axios.create({
  // baseURL: 'http://localhost:5000/api', // Make sure this matches your backend PORT
  baseURL: 'https://envdock.cloud/api', // Make sure this matches your backend PORT
  headers: {
    'Content-Type': 'application/json',
  },
});

// 2. Request Interceptor: Attach Token Automatically
api.interceptors.request.use(
  (config) => {
    let user;
    user = JSON.parse(localStorage.getItem('user'));
    if (user?.token) {
      config.headers.Authorization = `Bearer ${user?.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 3. Response Interceptor: Global Error Handling (Toasts)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A. Smart Error Message Extraction
    // Backend might send 'msg', 'message', or just a string. Catch them all.
    const message =
      error.response?.data?.msg ||
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Something went wrong';
    toast.error(message, {
      style: {
        background: '#333',
        color: '#fff',
        border: '1px solid #ffffff1a'
      },
    });

    // C. Handle 401 (Unauthorized / Token Invalid)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if we aren't already on login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    // D. Handle 402 (Payment Required) - THE FIX
    if (error.response?.status === 402) {
      if (!window.location.pathname.includes('/billing')) {
        window.location.href = '/dashboard/subscription?expired=true';
      }
    }

    return Promise.reject(error);
  }
);
// api.interceptors.response.use(
//   (response) => response, // Return successful responses as is
//   (error) => {
//     // Extract error message from backend
//     const message = error.response?.data?.message || 'Something went wrong';

//     // Show error toast
//     toast.error(message, {
//       style: {
//         background: '#333',
//         color: '#fff',
//         border: '1px solid #ffffff1a'
//       },
//     });

//     // Check for 401 (Unauthorized) -> Auto Logout logic could go here
//     if (error.response?.status === 401) {
//       localStorage.removeItem('token');
//       localStorage.removeItem('user');
//       // window.location.href = '/auth/login'; // Optional: Redirect to login
//     }

//     if (error.response && error.response.status === 402) {
//        // Force redirect to billing page
//        window.location.href = '/billing?expired=true';
//     }

//     return Promise.reject(error);
//   }
// );

// --- API Functions ---

export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    toast.success('Account created successfully!');
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    toast.success(`Welcome back, ${response.data.name}!`);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  usage: async () => {
    const response = await api.get('/auth/usage');
    return response.data;
  },
  forgot: async (values) => {
    const response = await api.post('/auth/forgotpassword', { email: values.email });
    return true;
  },
  resetPassword: async (values, token) => {
    await api.put(`/auth/resetpassword/${token}`, {
      password: values.password
    });
    return true;
  },

  googleAuth: async (token) => {
    // Sending the Google Access Token to backend
    const response = await api.post('/auth/google', { token });
    toast.success('Google Login Successful!');
    return response.data;
  },

  updateProfile: async (data) => {
    const res = await api.put('/auth/profile', data);
    return res.data;
  },

  updatePassword: async (data) => {
    const res = await api.put('/auth/password', data);
    return res.data;
  }
};

export const projectsAPI = {
  getAll: async () => {
    const res = await api.get('/projects');
    return res.data;
  },
  getOne: async (id) => {
    const res = await api.get(`/projects/${id}`);
    return res.data;
  },
  deleteProject: async (id) => {
    const res = await api.delete(`/projects/${id}`);
    toast.success('Project deleted successfully');
    return res.data;
  },
  create: async (data) => {
    const res = await api.post('/projects', data);
    toast.success('Project created!');
    return res.data;
  },
  // Team
  invite: async (projectId, data) => {
    const res = await api.post(`/projects/${projectId}/invite`, data);
    return res.data;
  },
  updateRole: async (projectId, userId, role) => {
    const res = await api.put(`/projects/${projectId}/members`, { userId, role });
    return res.data;
  },
  removeMember: async (projectId, userId) => {
    await api.delete(`/projects/${projectId}/members/${userId}`);
  },
  // Secrets
  getSecrets: async (projectId, env) => {
    const res = await api.get(`/projects/${projectId}/secrets/${env}`);
    return res.data || [];
  },
  updateSecrets: async (projectId, env, variables) => {
    const res = await api.post(`/projects/${projectId}/secrets/${env}`, { variables });
    toast.success(`Secrets updated for ${env}`);
    return res.data;
  },

  //  Audit Logs
  getLogs: async (projectId) => {
    const res = await api.get(`/projects/${projectId}/logs`);
    return res.data;
  },

  //  Service Tokens
  createToken: async (projectId, name, env) => {
    const res = await api.post(`/projects/${projectId}/tokens`, { name, environment: env });
    return res.data; // Returns { token: "kt_...", id: "..." }
  },

  getTokens: async (projectId) => {
    const res = await api.get(`/projects/${projectId}/tokens`);
    return res.data;
  },

  deleteToken: async (projectId, tokenId) => {
    const res = await api.delete(`/projects/${projectId}/tokens/${tokenId}`);
    return res.data;
  },

  // 3. Version History & Rollback
  getHistory: async (projectId, env) => {
    const res = await api.get(`/projects/${projectId}/secrets/${env}/history`);
    return res.data;
  },

  rollback: async (projectId, env, version) => {
    const res = await api.post(`/projects/${projectId}/secrets/${env}/rollback/${version}`);
    return res.data;
  },
  // =========================================================
  // NEW: INTEGRATIONS API
  // =========================================================

  // 1. Fetch all integrations for a project
  getIntegrations: async (projectId) => {
    const res = await api.get(`/projects/${projectId}/integrations`);
    return res.data;
  },

  // 2. Add a new integration
  addIntegration: async (projectId, data) => {
    const res = await api.post(`/projects/${projectId}/integrations`, data);
    return res.data;
  },

  // 3. Remove an integration
  deleteIntegration: async (projectId, integrationId) => {
    const res = await api.delete(`/projects/${projectId}/integrations/${integrationId}`);
    return res.data;
  }
};

export const dashboardAPI = {
  getStats: async () => {
    const res = await api.get('/dashboard/stats');
    return res.data;
  },

}

export const feedbackAPI = {
  submitReport: async (data) => {
    try {
      const response = await api.post('/feedback', data);
      toast.success('Report transmitted successfully.');
      return true;
    } catch (error) {
      // Extract error message if available
      const errMsg = error.response?.data?.msg || 'Transmission failed. Please try again.';
      toast.error(errMsg);
      throw error;
    }
  },
  getAllReports: async () => {
    const response = await api.get('/feedback');
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/feedback/${id}/status`, { status });
    toast.success(`Ticket status updated to ${status}`);
    return response.data;
  },
  // NEW: Get logged-in user's history
  getMyTickets: async () => {
    const response = await api.get('/feedback/my-history');
    return response.data;
  }
};

export const billingAPI = {
  /**
   * Creates a Stripe Checkout Session for new subscriptions
   * Returns: { url: "https://checkout.stripe.com/..." }
   */
  createCheckoutSession: async (payload) => {
    // We send payload (userId, planId, email) to the backend
    const res = await api.post('/billing/create-checkout-session', payload);
    return res.data;
  },

  /**
   * Creates a Stripe Portal Session for existing customers (update card, cancel)
   * Returns: { url: "https://billing.stripe.com/..." }
   */
  createPortalSession: async (payload) => {
    const res = await api.post('/billing/create-portal-session', payload);
    return res.data;
  }
};

export default api;